import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/core/supabase/client';
import { useAuth } from '@/core/auth/useAuth';
import { useCurrentCfaId, CFA_OVERRIDE_KEY } from '@/core/cfa/useCurrentCfaId';
import { useCallback } from 'react';

export interface CfaAssignment {
  cfa_id: string;
  cfa_nome: string;
  role: string;
}

interface SwitchCfaOptions {
  redirectTo?: string;
}

/**
 * Hook for the CFA switcher component.
 * Returns the list of CFAs assigned to the current user and a function to switch.
 * Only shows the switcher for users with 2+ CFA assignments.
 */
export function useCfaSwitcher() {
  const { session } = useAuth();
  const currentCfaId = useCurrentCfaId();

  const { data: assignments = [], isLoading } = useQuery<CfaAssignment[]>({
    queryKey: ['my-cfa-assignments', session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_cfa_assignments');
      if (error) throw error;
      return (data as CfaAssignment[]) || [];
    },
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    enabled: !!session?.access_token,
  });

  const currentCfa = assignments.find(a => a.cfa_id === currentCfaId);
  const cfaName = currentCfa?.cfa_nome ?? null;
  const isMultiCfa = assignments.length > 1;

  const switchCfa = useCallback(async (newCfaId: string, options?: SwitchCfaOptions) => {
    if (newCfaId === currentCfaId) {
      if (options?.redirectTo) {
        window.location.assign(options.redirectTo);
      }
      return;
    }

    // Persist the override
    localStorage.setItem(CFA_OVERRIDE_KEY, newCfaId);

    // Clear persisted caches to prevent cross-CFA data leakage
    try {
      const dbNames = ['idb-keyval', 'pasture-offline-db', 'kiosk-offline-db'];
      for (const dbName of dbNames) {
        indexedDB.deleteDatabase(dbName);
      }
    } catch (e) {
      // Best-effort cleanup, reload will follow
    }

    // Reload/redirect to clear all in-memory state
    if (options?.redirectTo) {
      window.location.assign(options.redirectTo);
      return;
    }
    window.location.reload();
  }, [currentCfaId]);

  return {
    currentCfaId,
    cfaName,
    assignments,
    isMultiCfa,
    switchCfa,
    isLoadingAssignments: isLoading,
  };
}
